// ── Media Library — shared, creator-scoped frontend data contract ──
// Backblaze B2 (via the existing chunked upload pipeline) is the real,
// already-working primary storage for uploaded video — this file never
// touches that. Everything here is ADDITIVE, frontend-only metadata
// (folders, archive state, backup preferences, display-name overrides)
// layered on top of real backend ContentSource records, plus accurate,
// clearly-labeled display of browser-local design assets (thumbnails,
// image posts, carousels, stories) that genuinely have no server record
// yet. Nothing here invents a file, a URL, or a server-side status.

import { getAllVariants } from '@/lib/optimizeVariants';
import { getAllScheduledPosts } from '@/lib/scheduleData';

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
function readJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
function writeJson(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage full/unavailable */ } }

// ── Folders (section 8) — logical, frontend-only organization. These are
// NOT physical B2/Dropbox paths; actual storage directory conventions are
// explicitly deferred to Level 3 (section 16). ──
const FOLDERS_KEY = (creatorId) => `consistency_media_folders_${creatorId}`;
const FOLDER_MEMBERS_KEY = (creatorId) => `consistency_media_folder_members_${creatorId}`;

export function getFolders(creatorId) { return readJson(FOLDERS_KEY(creatorId), []); }
export function createFolder(creatorId, { name, projectId = null, parentFolderId = null }) {
  const folders = getFolders(creatorId);
  const folder = { id: uid(), name, projectId, parentFolderId, createdAt: new Date().toISOString() };
  const next = [folder, ...folders];
  writeJson(FOLDERS_KEY(creatorId), next);
  return { folders: next, folder };
}
export function renameFolder(creatorId, folderId, name) {
  const next = getFolders(creatorId).map((f) => (f.id === folderId ? { ...f, name } : f));
  writeJson(FOLDERS_KEY(creatorId), next);
  return next;
}
export function deleteFolderRecord(creatorId, folderId) {
  const next = getFolders(creatorId).filter((f) => f.id !== folderId);
  writeJson(FOLDERS_KEY(creatorId), next);
  const members = getFolderMembers(creatorId);
  delete members[folderId];
  writeJson(FOLDER_MEMBERS_KEY(creatorId), members);
  return next;
}

export function getFolderMembers(creatorId) { return readJson(FOLDER_MEMBERS_KEY(creatorId), {}); }
export function getFolderAssetIds(creatorId, folderId) { return getFolderMembers(creatorId)[folderId] || []; }
export function addAssetToFolder(creatorId, folderId, assetId) {
  const members = getFolderMembers(creatorId);
  const ids = members[folderId] || [];
  if (!ids.includes(assetId)) writeJson(FOLDER_MEMBERS_KEY(creatorId), { ...members, [folderId]: [...ids, assetId] });
}
export function removeAssetFromFolder(creatorId, folderId, assetId) {
  const members = getFolderMembers(creatorId);
  writeJson(FOLDER_MEMBERS_KEY(creatorId), { ...members, [folderId]: (members[folderId] || []).filter((id) => id !== assetId) });
}

// ── Archive state (section 11) — no backend archive flag exists for
// ContentSource or ContentProject, so archiving assets, folders and
// projects is entirely frontend-only here, clearly separate from backend
// status. Never calls a backend endpoint to flip project/asset status. ──
const ARCHIVED_ASSETS_KEY = (creatorId) => `consistency_media_archived_assets_${creatorId}`;
const ARCHIVED_PROJECTS_KEY = (creatorId) => `consistency_media_archived_projects_${creatorId}`;
const ARCHIVED_FOLDERS_KEY = (creatorId) => `consistency_media_archived_folders_${creatorId}`;

function toggleInSet(key, id, archived) {
  const set = new Set(readJson(key, []));
  if (archived) set.add(id); else set.delete(id);
  writeJson(key, Array.from(set));
  return set;
}
export const getArchivedAssetIds = (creatorId) => new Set(readJson(ARCHIVED_ASSETS_KEY(creatorId), []));
export const setAssetArchived = (creatorId, assetId, archived) => toggleInSet(ARCHIVED_ASSETS_KEY(creatorId), assetId, archived);
export const getArchivedProjectIds = (creatorId) => new Set(readJson(ARCHIVED_PROJECTS_KEY(creatorId), []));
export const setProjectArchived = (creatorId, projectId, archived) => toggleInSet(ARCHIVED_PROJECTS_KEY(creatorId), projectId, archived);
export const getArchivedFolderIds = (creatorId) => new Set(readJson(ARCHIVED_FOLDERS_KEY(creatorId), []));
export const setFolderArchived = (creatorId, folderId, archived) => toggleInSet(ARCHIVED_FOLDERS_KEY(creatorId), folderId, archived);

// ── Display-name overlay (section 9 "Rename") — there is no backend
// rename endpoint for ContentSource, so a rename here is a LOCAL label
// shown throughout the Media Library, never a server-side filename/title
// change. Local design records (thumbnails/posts) ARE fully local already
// and are renamed directly on their own record instead (see ImagesAndPosts.jsx). ──
const DISPLAY_NAMES_KEY = (creatorId) => `consistency_media_display_names_${creatorId}`;
export const getDisplayNameOverrides = (creatorId) => readJson(DISPLAY_NAMES_KEY(creatorId), {});
export function setDisplayNameOverride(creatorId, assetId, name) {
  const next = { ...getDisplayNameOverrides(creatorId), [assetId]: name };
  writeJson(DISPLAY_NAMES_KEY(creatorId), next);
  return next;
}

// ── Local-only design assets (section 5) — thumbnails, image posts,
// carousels and Stories are created in Images & Posts and currently have
// NO backend record at all; they live in `consistency_images_posts_{projectId}`.
// Surfaced here honestly tagged `localOnly: true` — never presented as a
// server-persisted file. ──
export function getLocalDesignAssets(projectId, projectName) {
  const designs = readJson(`consistency_images_posts_${projectId}`, []);
  return designs.map((d) => ({
    id: `design-${d.id}`,
    rawId: d.id,
    name: d.name || 'Untitled design',
    assetKind: d.assetType || 'image_post',
    type: 'design',
    localOnly: true,
    projectId,
    projectName,
    createdAt: d.createdAt || null,
    thumbnailUrl: d.variants?.[0]?.pages?.[0]?.elements?.find((e) => e.type === 'image')?.src || null,
    variantCount: (d.variants || []).length,
    aspectRatio: d.variants?.[0]?.aspectRatio || null,
    sourceRef: d.sourceRef || null,
    pages: d.variants?.[0]?.pages || [],
  }));
}

// ── Dependency check before permanent deletion (section 10) — best-effort,
// using only data this browser already has locally (Optimize variants,
// Schedule posts, thumbnail associations). There is no backend dependency
// API, so Clip usage specifically can't be verified here — called out
// explicitly rather than silently skipped. ──
export function checkAssetDependencies(creatorId, assetId) {
  const warnings = [];
  const variants = getAllVariants().filter((v) => v.assetId === assetId && v.creatorId === creatorId);
  if (variants.length > 0) warnings.push(`${variants.length} Optimize variant(s) reference this asset.`);
  const posts = getAllScheduledPosts().filter((p) => p.assetId === assetId);
  if (posts.length > 0) warnings.push(`${posts.length} scheduled or published post(s) reference this asset.`);
  return { warnings, uncheckable: ['Clips created from this video (no dependency-check API exists yet — Level 3)'] };
}

// ── Dropbox backup (sections 13/14) — preferences only; no background
// jobs run from here, and no transfer is ever marked successful unless a
// real, confirmed backend transfer exists (none does yet — see
// `lib/mediaLibrary.js` callers for the honest Level 3 messaging). ──
const BACKUP_PREFS_KEY = (creatorId) => `consistency_media_backup_prefs_${creatorId}`;
export function getBackupPrefs(creatorId) {
  return readJson(BACKUP_PREFS_KEY(creatorId), {
    autoEnabled: false, frequency: 'weekly', scopeProjectIds: [], destination: 'dropbox', history: [],
  });
}
export function setBackupPrefs(creatorId, patch) {
  const next = { ...getBackupPrefs(creatorId), ...patch };
  writeJson(BACKUP_PREFS_KEY(creatorId), next);
  return next;
}
